import type { AppState } from '../state/store';
import { renderDashboard } from './dashboard';
import { renderDocs } from './docsView';
import { renderHowToPlay } from './howToPlayView';
import { renderLanding } from './landingView';
import { renderSpectate } from './spectateView';
import { renderRun } from './runView';
import { renderInventory } from './inventoryView';
import { renderMerchant } from './merchantView';
import { renderWaystation } from './waystationView';
import { renderMarketplace } from './marketplaceView';
import { renderCashout } from './cashoutView';
import { renderSpinner } from './spinnerView';
import { renderCosmetics } from './cosmeticsView';
import { renderRoadmap } from './roadmapView';
import { renderAdmin } from './adminView';
import { btn, busyBar, errorBanner, esc, noticeBanner, shortAddr } from './components';
import { headerActionButtons } from './headerActions';

export interface RunRenderState {
  stageCanvas: HTMLCanvasElement | null;
  textGridOpen: boolean;
  d2PreviewOpen: boolean;
}

export function captureRunRenderState(root: Pick<HTMLElement, 'querySelector'>): RunRenderState {
  return {
    stageCanvas: root.querySelector<HTMLCanvasElement>('[data-gamefeel-stage]'),
    textGridOpen: root.querySelector<HTMLElement>('.text-grid-debug')?.hasAttribute('open') ?? false,
    d2PreviewOpen: root.querySelector<HTMLElement>('.gamefeel-panel')?.hasAttribute('open') ?? false,
  };
}

function restoreDetailsOpen(root: Pick<HTMLElement, 'querySelector'>, selector: string, open: boolean): void {
  if (!open) return;
  root.querySelector<HTMLElement>(selector)?.setAttribute('open', '');
}

export function restoreRunRenderState(root: Pick<HTMLElement, 'querySelector'>, state: RunRenderState): void {
  const nextCanvas = root.querySelector<HTMLCanvasElement>('[data-gamefeel-stage]');
  if (state.stageCanvas && nextCanvas && state.stageCanvas !== nextCanvas) {
    const label = nextCanvas.getAttribute('aria-label');
    if (label) state.stageCanvas.setAttribute('aria-label', label);
    state.stageCanvas.className = nextCanvas.className;
    nextCanvas.replaceWith(state.stageCanvas);
  }
  restoreDetailsOpen(root, '.text-grid-debug', state.textGridOpen);
  restoreDetailsOpen(root, '.gamefeel-panel', state.d2PreviewOpen);
}

function header(state: AppState): string {
  const nav = state.session
    ? `<nav class="nav">
         ${navBtn('Waystation', 'waystation', state.view)}
         ${navBtn('Dashboard', 'dashboard', state.view)}
         ${navBtn('Run', 'run', state.view, !state.run && !state.terminal)}
         ${navBtn('Inventory', 'inventory', state.view)}
         ${navBtn('Merchant', 'merchant', state.view)}
         ${navBtn('Marketplace', 'marketplace', state.view)}
         ${navBtn('Gold Exchange', 'cashout', state.view)}
         ${navBtn('Spinner', 'spinner', state.view)}
         ${navBtn('Cosmetics', 'cosmetics', state.view)}
         ${navBtn('Roadmap', 'roadmap', state.view)}
         ${navBtn('Docs', 'docs', state.view)}
       </nav>`
    : `<nav class="nav">
         ${navBtn('Home', 'landing', state.view)}
         ${navBtn('How To Play', 'how-to-play', state.view)}
         ${navBtn('Roadmap', 'roadmap', state.view)}
         ${navBtn('Docs', 'docs', state.view)}
         ${navBtn('Spectate', 'spectate', state.view)}
         ${navBtn('Connect', 'dashboard', state.view)}
       </nav>`;
  const wallet = state.session
    ? `<div class="whoami"><code>${esc(shortAddr(state.walletAddress))}</code> ${btn('Sign out', 'logout', {}, { variant: 'ghost' })}</div>`
    : '';
  return `
    <header class="topbar">
      <div class="brand"><span class="ember-dot" aria-hidden="true"></span> Gravelight</div>
      ${nav}
      <div class="topbar-actions">${headerActionButtons('topbar')}</div>
      ${wallet}
    </header>`;
}

function navBtn(label: string, view: AppState['view'], current: AppState['view'], disabled = false): string {
  const cls = view === current ? 'navbtn navbtn--active' : 'navbtn';
  const active = view === current ? 'aria-current="page"' : '';
  const disabledAttr = disabled ? 'disabled aria-disabled="true"' : '';
  return `<button type="button" class="${cls}" data-nav-view="${esc(view)}" data-act="goto-${view}" ${active} ${disabledAttr}>${esc(label)}</button>`;
}

function viewBody(state: AppState): string {
  if (state.view === 'landing') return renderLanding(state);
  if (state.view === 'docs') return renderDocs(state);
  if (state.view === 'how-to-play') return renderHowToPlay(state);
  if (state.view === 'roadmap') return renderRoadmap(state);
  if (state.view === 'spectate') return renderSpectate(state);
  if (state.view === 'run') return renderRun(state);
  if (state.view === 'inventory') return renderInventory(state);
  if (state.view === 'merchant') return renderMerchant(state);
  if (state.view === 'waystation') return renderWaystation(state);
  if (state.view === 'marketplace') return renderMarketplace(state);
  if (state.view === 'cashout') return renderCashout(state);
  if (state.view === 'spinner') return renderSpinner(state);
  if (state.view === 'cosmetics') return renderCosmetics(state);
  if (state.view === 'admin') return renderAdmin(state);
  return renderDashboard(state);
}

function bindConfirmModal(state: AppState): string {
  const pending = state.pendingBindEquip;
  if (!pending) return '';
  return `
    <div class="modal-backdrop" role="presentation">
      <section class="modal-card bind-confirmation" role="dialog" aria-modal="true" aria-labelledby="bind-confirmation-title">
        <h2 id="bind-confirmation-title">Bind this item?</h2>
        <p><strong>${esc(pending.title)}</strong></p>
        <p>Equipping this item will bind it to your account. Bound items cannot be listed on the player marketplace or traded to other players. You can still salvage bound gear to the Merchant for a small fixed Gold return.</p>
        <div class="btn-row">
          ${btn('Cancel', 'cancel-bind-equip', {}, { variant: 'ghost' })}
          ${btn('Equip & Bind', 'confirm-bind-equip', {}, { variant: 'primary' })}
        </div>
      </section>
    </div>`;
}

/** Render the whole app into `root` from the current state. */
export function render(root: HTMLElement, state: AppState): void {
  const preserved = captureRunRenderState(root);
  if (state.view === 'landing') {
    root.innerHTML = `<div class="app app--home">${renderLanding(state)}</div>`;
    restoreRunRenderState(root, preserved);
    return;
  }

  root.innerHTML = `
    <div class="app">
      ${header(state)}
      <main class="content">
        ${busyBar(state.busy)}
        ${errorBanner(state.error)}
        ${noticeBanner(state.notice)}
        ${viewBody(state)}
      </main>
      ${bindConfirmModal(state)}
      <footer class="foot">
        ${state.devMode ? `<span>API · <code>${esc(state.apiBase)}</code></span><span class="pill pill--warn">DEV build</span>` : ''}
      </footer>
    </div>`;
  restoreRunRenderState(root, preserved);
}
